import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collect } from 'collect.js';
import { Button, DatePicker, Dropdown, Radio, Space } from 'antd';
import { CalendarOutlined, CloseOutlined, DownOutlined } from '@ant-design/icons';
import moment from 'moment-timezone';
import useHelpers from '../../hooks/context/useHelpers';
import { Stringable } from '../../services/helpers/Stringable';
import { isSameDateRange } from '../../services/helpers/DateRangeHelpers';

const { RangePicker } = DatePicker;

export const variant_dropdown = 'dropdown';
export const variant_radio_group = 'radio_group';

export const filter_today = 'today';
export const filter_yesterday = 'yesterday';
export const filter_this_week = 'this_week';
export const filter_last_week = 'last_week';
export const filter_this_month = 'this_month';
export const filter_this_year = 'this_year';
export const filter_last_month = 'last_month';
export const filter_last_30_days = 'last_30_days';
export const filter_all = 'all';

export const filterFactory = (filter) =>
  ({
    [filter_today]: {
      start_date: moment().startOf('day').utc().format(),
      end_date: moment().endOf('day').utc().format(),
    },
    [filter_yesterday]: {
      start_date: moment().subtract(1, 'day').utc().startOf('day').format(),
      end_date: moment().subtract(1, 'day').utc().endOf('day').format(),
    },
    [filter_this_week]: {
      start_date: moment().startOf('week').utc().format(),
      end_date: moment().endOf('week').utc().format(),
    },
    [filter_last_week]: {
      start_date: moment().subtract(1, 'week').utc().startOf('week').format(),
      end_date: moment().subtract(1, 'week').utc().endOf('week').format(),
    },
    [filter_this_month]: {
      start_date: moment().startOf('month').utc().format(),
      end_date: moment().endOf('month').utc().format(),
    },
    [filter_last_month]: {
      start_date: moment().subtract(1, 'month').utc().startOf('month').format(),
      end_date: moment().subtract(1, 'month').utc().endOf('month').format(),
    },
    [filter_last_30_days]: {
      start_date: moment().subtract(30, 'days').utc().startOf('day').format(),
      end_date: moment().utc().endOf('day').format(),
    },
    [filter_all]: {
      start_date: null,
      end_date: null,
    },
  })[filter];

const _filters = [
  filter_today,
  filter_yesterday,
  filter_this_week,
  filter_last_week,
  filter_this_month,
  filter_last_month,
  filter_last_30_days,
  filter_all,
];

/**
 * @param {object} props
 * @param {Function} props.onChange Receives the selected filter, or a { start_date, end_date } custom range.
 * @param {object|null} props.initialValue The filter the parent already holds when the picker mounts. Read
 *   on mount only, to skip announcing a selection the parent already has; it does not control the picker.
 */
export default function DateRangePicker({
  onChange,
  initialValue = null,
  variant = variant_radio_group,
  defaultFilter = null,
  customFilters = [],
  excludeFilters = [],
  hideCustomRange = false,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Filters:DateRangePicker' });

  const { dateInputFormat, getActiveTab, setActiveTab } = useHelpers();

  const filters = useMemo(
    () =>
      collect(_filters)
        .map((item) => ({ id: item, name: t(`filters.${item}`), ...filterFactory(item) }))
        .when(
          excludeFilters.includes('*'),
          (collection) => collection.reject(() => true),
          (collection) => collection.whereNotIn('id', excludeFilters),
        )
        .merge(
          customFilters.map((item) => ({
            ...item,
            name: item.name ?? t(`filters.${item.period}`, Stringable.toHuman(item.period)),
          })),
        ),
    [customFilters, excludeFilters, t],
  );

  const [selectedFilter, setSelectedFilter] = useState(
    filters.firstWhere('id', defaultFilter ?? getActiveTab(`${window.location.pathname}.date_filter`)),
  );
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [dateRange, setDateRange] = useState(null);

  const isMounted = useRef(false);

  useEffect(() => {
    // The initial selection is announced so a parent without a filter gets one, but a parent that
    // already holds this value would only re-render and refetch with an identical request.
    const isInitial = !isMounted.current;
    isMounted.current = true;

    if (dateRange) {
      onChange({
        start_date: moment(dateRange[0].format()).startOf('day').format(),
        end_date: moment(dateRange[1].format()).endOf('day').format(),
      });

      return;
    }

    if (!selectedFilter || (isInitial && initialValue && isSameDateRange(initialValue, selectedFilter))) {
      return;
    }

    onChange(selectedFilter);
  }, [selectedFilter, dateRange]);

  const onSelect = (filter) => {
    setActiveTab(filter, `${window.location.pathname}.date_filter`);
    setSelectedFilter(filters.firstWhere('id', filter));
  };

  function toggleCustomFilter() {
    setShowDateRangePicker(false);
    setDateRange(null);
  }

  const renderVariant = {
    [variant_dropdown]: () => (
      <Dropdown
        menu={{
          items: filters.map((filter) => ({ key: filter.id, label: filter.name })),
          selectable: true,
          selectedKeys: selectedFilter ? [selectedFilter.id] : null,
          onSelect: (e) => onSelect(e.key),
        }}
      >
        <Button>
          <Space>
            {selectedFilter?.name ?? 'Select period'}
            <DownOutlined />
          </Space>
        </Button>
      </Dropdown>
    ),
    [variant_radio_group]: () => (
      <Radio.Group value={selectedFilter?.id} onChange={(e) => onSelect(e.target.value)}>
        {filters.map((filter) => (
          <Radio.Button key={filter.id} value={filter.id} className="px-sm">
            {filter.name}
          </Radio.Button>
        ))}
      </Radio.Group>
    ),
  }[variant];

  return (
    <div className="flex items-center gap-xs">
      {!showDateRangePicker && (
        <>
          {renderVariant()}
          {!excludeFilters.includes('*') && !hideCustomRange && (
            <Button icon={<CalendarOutlined />} onClick={() => setShowDateRangePicker(true)} />
          )}
        </>
      )}
      {showDateRangePicker && (
        <>
          <RangePicker
            format={dateInputFormat}
            value={dateRange}
            onChange={setDateRange}
            icon={<CalendarOutlined />}
            defaultOpen
          />
          <Button icon={<CloseOutlined />} onClick={toggleCustomFilter} />
        </>
      )}
    </div>
  );
}
